import React, { useState } from 'react'
import { DatePicker, Select, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import moment, { Moment } from 'moment'
import { useLocation } from 'react-router'
import Button from '@/components/button/button'
import SearchInput from '@/components/search-input/search-input'
import { Expaned } from '@/components/layout/layout'
import $msg from '@/utils/message'
import { layerManage, showLayer } from '@/utils/layer'
import { getConsumedRechargePayments, getMemberRechargeRecords } from '../api'
import MemberRecordDetail, { MemberRecordDetailSection } from '../member-record-detail/member-record-detail'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'
import MemberHeaderActions from '../member-header-actions'
import Auth from '@/views/main/components/auth/auth'
import MemberBalanceRefundModal from '../member-detail/member-balance-refund-modal'
import { getRechargePaymentStatusText, hasRechargeRefundOrder, isRechargePaid } from './recharge-record-utils'

import './recharge-record.scss'

const sourceMap: { [key: number]: string } = {
    [-1]: '未知来源',
    0: '收银台',
    1: '零售通',
    2: '会员充值',
    3: '找零存入余额',
}

const formatDateTime = (date?: string) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '--')
const formatAmount = (amount?: string | number) =>
    amount === undefined || amount === null || amount === '' ? '--' : Number(amount).toFixed(2)
const getMemberName = (member?: any) => member?.name || member?.user?.nickname || '--'
const getPaymentName = (record: any) =>
    record.payment?.custom_payment?.name || record.payment?.payment_type_text || record.payment_type_text || '--'
const getOperatorName = (creator?: any) =>
    creator?.name ||
    creator?.realname ||
    creator?.nickname ||
    creator?.user?.realname ||
    creator?.user?.nickname ||
    '线上自助充值'
const RechargeRecord: React.FC = () => {
    const location = useLocation()
    const initialStartDate = new URLSearchParams(location.search).get('start_date')
    const initialEndDate = new URLSearchParams(location.search).get('end_date')
    const hasInitialDate = !!(initialStartDate && initialEndDate)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [keyword, setKeyword] = useState('')
    const [paymentFilters, setPaymentFilters] = useState<string[]>([])
    const [refundRecord, setRefundRecord] = useState<any>()
    const [searchKey, setSearchKey] = useState(0)
    const [date, setDate] = useState<string[]>(() =>
        hasInitialDate
            ? [initialStartDate as string, initialEndDate as string]
            : [dayjs().subtract(1, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
    )
    const [dateValue, setDateValue] = useState<[Moment | null, Moment | null] | null>(() =>
        hasInitialDate
            ? [moment(initialStartDate as string), moment(initialEndDate as string)]
            : [moment().subtract(1, 'month'), moment()],
    )
    const resetFilter = () => {
        setPage(1)
        setKeyword('')
        setPaymentFilters([])
        setSearchKey(value => value + 1)
        setDate([dayjs().subtract(1, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
        setDateValue([moment().subtract(1, 'month'), moment()])
    }

    const paymentOptions = useRequest(
        () => getConsumedRechargePayments({ start_date: date[0], end_date: date[1], order_type: 1 }),
        {
            ready: !!date[0] && !!date[1],
            refreshDeps: [date],
            onError: e => $msg('error', e.message || '获取支付方式失败'),
        },
    )

    const availablePayments: Array<{ payment_type: number; payment_name: string; custom_payment_id: number | null }> =
        paymentOptions.data || []

    const req = useRequest(
        () =>
            getMemberRechargeRecords({
                page,
                limit: pageSize,
                keyword: keyword || undefined,
                start_date: date[0],
                end_date: date[1],
                payment_list: paymentFilters.length
                    ? paymentFilters.filter(value => value.startsWith('type-')).map(value => Number(value.slice(5)))
                    : undefined,
                custom_payment_ids: paymentFilters.length
                    ? paymentFilters.filter(value => value.startsWith('custom-')).map(value => Number(value.slice(7)))
                    : undefined,
            }),
        {
            refreshDeps: [page, pageSize, keyword, date, paymentFilters],
            onError: e => $msg('error', e.message || '获取充值记录失败'),
        },
    )

    const showRechargeDetail = (record: any) => {
        const rechargeAmount = Number(record.recharge_amount || 0)
        const rechargeBonus = Number(record.recharge_bonus || 0)
        const isRefunded = hasRechargeRefundOrder(record)
        const sections: MemberRecordDetailSection[] = [
            {
                title: '订单信息',
                items: [
                    { label: '订单编号', value: record.order_no || '--', wide: true },
                    { label: '充值时间', value: formatDateTime(record.create_time) },
                    { label: '操作员', value: getOperatorName(record.creator) },
                    { label: '充值来源', value: sourceMap[record.recharge_source] || '--' },
                    { label: '订单状态', value: getRechargePaymentStatusText(record) },
                ],
            },
            {
                title: '会员信息',
                items: [
                    { label: '会员', value: getMemberName(record.member) },
                    { label: '手机号', value: record.member?.phone || '--' },
                ],
            },
            {
                title: '充值与收款',
                items: [
                    { label: '充值金额', value: `${formatAmount(rechargeAmount)} 元` },
                    { label: '赠送金额', value: `${formatAmount(rechargeBonus)} 元` },
                    { label: '余额实际到账', value: `${formatAmount(rechargeAmount + rechargeBonus)} 元` },
                    { label: '实付金额', value: `${formatAmount(record.actually_paid)} 元` },
                    { label: '支付方式', value: getPaymentName(record) },
                    { label: '退款状态', value: isRefunded ? '已退款' : '未退款' },
                    { label: '备注', value: record.remark || '--', wide: true },
                ],
            },
        ]

        if (isRefunded) {
            sections.push({
                title: '退款信息',
                items: [
                    { label: '退款单号', value: record.refund_order.order_no || '--', wide: true },
                    { label: '退款时间', value: formatDateTime(record.refund_order.refund_time) },
                    { label: '退款金额', value: `${formatAmount(record.refund_order.refund_money)} 元` },
                    { label: '退赠送金', value: `${formatAmount(record.refund_order.refund_bonus_money)} 元` },
                    { label: '退款原因', value: record.refund_order.refund_reason || '--', wide: true },
                ],
            })
        }

        showLayer(<MemberRecordDetail title="充值记录详情" sections={sections} onClose={() => layerManage.pop()} />)
    }

    const columns: ColumnsType<any> = [
        {
            title: '#',
            key: 'index',
            width: 56,
            render: (_value, _record, index) => (page - 1) * pageSize + index + 1,
        },
        { title: '时间', dataIndex: 'create_time', width: 170, render: formatDateTime },
        { title: '订单编号', dataIndex: 'order_no', width: 220, render: value => value || '--' },
        { title: '会员', dataIndex: 'member', width: 160, render: getMemberName },
        { title: '会员手机号', dataIndex: ['member', 'phone'], width: 152, render: value => value || '--' },
        {
            title: '充值金额',
            dataIndex: 'recharge_amount',
            width: 112,
            render: formatAmount,
        },
        {
            title: '赠送金额',
            dataIndex: 'recharge_bonus',
            width: 112,
            render: formatAmount,
        },
        { title: '支付方式', width: 132, render: (_value, record) => getPaymentName(record) },
        { title: '来源', dataIndex: 'recharge_source', width: 112, render: value => sourceMap[value] || '--' },
        { title: '操作员', dataIndex: 'creator', width: 140, render: getOperatorName },
        {
            title: '订单状态',
            key: 'payment_status',
            width: 112,
            render: (_value, record) => getRechargePaymentStatusText(record),
        },
        {
            title: '实付金额',
            dataIndex: 'actually_paid',
            width: 112,
            render: formatAmount,
        },
        {
            title: '操作',
            width: 150,
            fixed: 'right',
            render: (_value, record) => (
                <>
                    <span className="action" onClick={() => showRechargeDetail(record)}>详情</span>
                    {record.member?.id && isRechargePaid(record) && !hasRechargeRefundOrder(record) ? (
                        <Auth perssion={51} onClick={() => setRefundRecord(record)}>
                            <span className="action" style={{ marginLeft: 12 }}>退款</span>
                        </Auth>
                    ) : null}
                </>
            ),
        },
    ]

    return (
        <Expaned className="member-recharge-record">
            <MemberHeaderActions>
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="筛选支付方式"
                    style={{ minWidth: 180, maxWidth: 300 }}
                    value={paymentFilters}
                    loading={paymentOptions.loading}
                    onChange={(values: string[]) => {
                        setPage(1)
                        setPaymentFilters(values)
                    }}
                >
                    {availablePayments.map(item => (
                        <Select.Option
                            key={
                                item.custom_payment_id != null
                                    ? `custom-${item.custom_payment_id}`
                                    : `type-${item.payment_type}`
                            }
                            value={
                                item.custom_payment_id != null
                                    ? `custom-${item.custom_payment_id}`
                                    : `type-${item.payment_type}`
                            }
                        >
                            {item.payment_name}
                        </Select.Option>
                    ))}
                </Select>
                <DatePicker.RangePicker
                    dateRender={current => (
                        <div className="ant-picker-cell-inner" style={{ borderRadius: '50%' }}>
                            {current.date()}
                        </div>
                    )}
                    value={dateValue}
                    allowClear
                    suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                    onChange={(momentValue, dateString) => {
                        setPage(1)
                        setDateValue(momentValue)
                        setDate(dateString[0] ? dateString : [])
                    }}
                />
                <div className="member-recharge-record__search">
                    <SearchInput
                        key={searchKey}
                        placeholder="输入会员昵称/手机号/订单号"
                        onSearch={val => {
                            setPage(1)
                            setKeyword(val)
                        }}
                        onDelete={() => {
                            setPage(1)
                            setKeyword('')
                        }}
                    />
                </div>
                <Button className="member-manage__toolbar-button" types="primary" onClick={resetFilter}>
                    重置
                </Button>
                <Button
                    className="member-manage__toolbar-button"
                    types="primary"
                    loading={req.loading}
                    onClick={() => req.refresh()}
                >
                    刷新
                </Button>
            </MemberHeaderActions>
            <Expaned data-column data-overflow-hidden className="member-recharge-record__content">
                <div className="table-container member-recharge-record__table">
                    <Table
                        bordered
                        rowKey={(record, index) => record.id || record.order_no || index}
                        loading={req.loading}
                        columns={columns}
                        dataSource={req.data?.recharge_order_list || []}
                        pagination={getMemberTablePagination({
                            current: page,
                            pageSize,
                            total: getTableTotal(req.data?.pagination),
                            onChange: setPage,
                            onShowSizeChange: (nextPage, nextSize) => {
                                setPage(nextPage)
                                setPageSize(nextSize)
                            },
                        })}
                        scroll={{ x: 'max-content', y: 'calc(100vh - 200px)' }}
                    />
                </div>
            </Expaned>
            {refundRecord ? (
                <MemberBalanceRefundModal
                    visible
                    memberId={refundRecord.member.id}
                    rechargeOrder={refundRecord}
                    onCancel={() => setRefundRecord(undefined)}
                    onSuccess={() => {
                        setRefundRecord(undefined)
                        req.refresh()
                    }}
                />
            ) : null}
        </Expaned>
    )
}

export default RechargeRecord
